import React, { useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../shared/stores/authStore'
import { BookOpen, Home, Library, User, LogIn, PenLine, Edit3, Bot, GitFork, LayoutTemplate, Bookmark, MessageSquare, Star, CreditCard, MoreHorizontal, X, Lightbulb, Store, BookMarked, Headphones, ChevronDown, Compass } from 'lucide-react'
import { LanguageSwitcher } from '../../components/LanguageSwitcher'
import NotificationBell from '../../components/NotificationBell'

const UserLayout: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { user } = useAuthStore()
  const [moreOpen, setMoreOpen] = useState(false)
  const [openCategory, setOpenCategory] = useState<string | null>(null)
  const [categoryTimer, setCategoryTimer] = useState<ReturnType<typeof setTimeout> | null>(null)

  /** 延迟关闭下拉（给用户从按钮移到菜单的时间） */
  const closeCategoryWithDelay = () => {
    const timer = setTimeout(() => setOpenCategory(null), 200)
    setCategoryTimer(timer)
  }

  const cancelCloseCategory = () => {
    if (categoryTimer) {
      clearTimeout(categoryTimer)
      setCategoryTimer(null)
    }
  }

  /** 判断某个下拉分组是否有子项处于激活状态 */
  const isCategoryActive = (items: { path: string }[]) =>
    items.some((item) => isActive(item.path))

  // 桌面端分类导航分组
  const navCategories = [
    {
      label: t('nav.browse', '浏览'),
      icon: Compass,
      items: [
        { path: '/home', label: t('nav.home'), icon: Home },
        { path: '/novels', label: t('nav.novels'), icon: Library },
        { path: '/audiobooks', label: '有声书', icon: Headphones },
      ],
    },
    {
      label: t('nav.createCategory', '创作'),
      icon: PenLine,
      items: [
        { path: '/write', label: t('nav.write'), icon: Edit3 },
        { path: '/agents', label: t('nav.agents'), icon: Bot },
        { path: '/kg', label: t('nav.kg'), icon: GitFork },
        { path: '/templates', label: t('nav.templates'), icon: LayoutTemplate },
      ],
    },
    {
      label: t('nav.community', '社区'),
      icon: MessageSquare,
      items: [
        { path: '/community', label: t('nav.community'), icon: MessageSquare },
        { path: '/reviews', label: t('nav.reviews'), icon: Star },
      ],
    },
    {
      label: t('nav.profile', '个人'),
      icon: User,
      items: [
        { path: '/bookshelf', label: t('nav.bookshelf'), icon: Bookmark },
        { path: '/credits', label: '积分', icon: CreditCard },
        { path: '/pricing', label: t('nav.pricing'), icon: CreditCard },
        { path: '/profile', label: t('nav.profile'), icon: User },
      ],
    },
    {
      label: t('nav.shop', '商城'),
      icon: Store,
      items: [
        { path: '/marketplace', label: '资产市场', icon: Store },
      ],
    },
  ]

  // Mobile primary bottom tab items (4 main + More button)
  // 书架 | 首页 | 书库 | 我的  — 符合小程序/H5设计
  const mobilePrimaryLinks = [
    { path: '/bookshelf', label: t('nav.bookshelf'), icon: BookMarked },
    { path: '/home', label: t('nav.home'), icon: Home },
    { path: '/novels', label: t('nav.novels'), icon: Library },
    { path: '/profile', label: t('nav.profile'), icon: User },
  ]

  // Overflow items for the "More" bottom sheet drawer
  const moreLinks = [
    { path: '/community', label: t('nav.community'), icon: MessageSquare },
    { path: '/write', label: t('nav.write'), icon: Edit3 },
    { path: '/credits', label: '积分', icon: CreditCard },
    { path: '/audiobooks', label: '有声书', icon: Headphones },
    { path: '/marketplace', label: '资产市场', icon: Store },
    { path: '/bookshelf', label: t('nav.bookshelf'), icon: Bookmark },
    { path: '/agents', label: t('nav.agents'), icon: Bot },
    { path: '/kg', label: t('nav.kg'), icon: GitFork },
    { path: '/templates', label: t('nav.templates'), icon: LayoutTemplate },
    { path: '/inspiration', label: t('nav.inspiration') || '灵感', icon: Lightbulb },
    { path: '/reviews', label: t('nav.reviews'), icon: Star },
    { path: '/pricing', label: t('nav.pricing'), icon: CreditCard },
  ]

  const isActive = (path: string) => {
    if (path === '/home') return location.pathname === '/home'
    return location.pathname.startsWith(path)
  }

  // "More" button is highlighted when any overflow item is active
  const isMoreActive = moreLinks.some((link) => isActive(link.path))

  return (
    <div className="min-h-screen flex flex-col bg-[#faf5f0]">
      {/* ===== Desktop Top Navbar (unchanged) ===== */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-amber-100/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/home" className="flex items-center gap-2 group">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#6366f1] to-[#818cf8] flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                <PenLine className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-gray-800">
                <span className="text-[#6366f1]">网文</span>AI
              </span>
            </Link>

            {/* Desktop category nav with dropdowns */}
            <nav className="hidden md:flex items-center gap-1">
              {navCategories.map((cat) => {
                const CatIcon = cat.icon
                const catActive = isCategoryActive(cat.items)
                const isOpen = openCategory === cat.label
                return (
                  <div
                    key={cat.label}
                    className="relative"
                    onMouseEnter={() => {
                      cancelCloseCategory()
                      setOpenCategory(cat.label)
                    }}
                    onMouseLeave={closeCategoryWithDelay}
                    // 透明桥接区：消除按钮与下拉菜单之间的缝隙
                    style={{
                      paddingBottom: '4px',
                      marginBottom: '-4px',
                      backgroundClip: 'padding-box',
                    }}
                  >
                    <button
                      onClick={() => {
                        if (openCategory === cat.label) {
                          setOpenCategory(null)
                        } else {
                          cancelCloseCategory()
                          setOpenCategory(cat.label)
                        }
                      }}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        catActive || isOpen
                          ? 'bg-[#6366f1]/10 text-[#6366f1] shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100/60'
                      }`}
                    >
                      <CatIcon className="w-4 h-4" />
                      <span>{cat.label}</span>
                      <ChevronDown
                        className={`w-3 h-3 transition-transform duration-200 ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {/* Dropdown menu */}
                    {isOpen && (
                      <div className="absolute top-full left-0 mt-1.5 w-44 bg-white rounded-xl shadow-lg border border-amber-100/60 py-1.5 z-50 overflow-hidden">
                        {cat.items.map((item) => {
                          const ItemIcon = item.icon
                          const itemActive = isActive(item.path)
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setOpenCategory(null)}
                              className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                itemActive
                                  ? 'text-[#6366f1] bg-[#6366f1]/5 font-medium'
                                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                              }`}
                            >
                              <ItemIcon className="w-4 h-4 shrink-0" />
                              {item.label}
                            </Link>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </nav>

            {/* Right-side actions */}
            <div className="flex items-center gap-2">
              {/* 通知铃铛 */}
              <NotificationBell />
              <LanguageSwitcher />
              <Link
                to="/novels"
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-[#d97706] hover:bg-amber-50 transition-all duration-200"
              >
                <BookOpen className="w-4 h-4" />
                {t('nav.startReading')}
              </Link>
              {user ? (
                <Link
                  to="/profile"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-[#6366f1] bg-[#6366f1]/10 hover:bg-[#6366f1]/20 transition-all duration-200"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline max-w-[120px] truncate">{user.username || user.email}</span>
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#6366f1] to-[#818cf8] hover:from-[#5558e6] hover:to-[#7377f0] shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <LogIn className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('auth.login')}</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ===== Main Content ===== */}
      {/* pb-24 on mobile to clear the fixed bottom nav + FAB */}
      <main className="flex-1 pb-24 md:pb-0">
        <Outlet />
      </main>

      {/* ===== Footer (hidden on mobile) ===== */}
      <footer className="hidden md:block bg-white border-t border-amber-100/60 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-gray-500">
              <PenLine className="w-4 h-4 text-[#6366f1]" />
              <span className="text-sm">{t('footer.tagline')}</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <a href="#" className="hover:text-[#6366f1] transition-colors">{t('footer.about')}</a>
              <a href="#" className="hover:text-[#6366f1] transition-colors">{t('footer.terms')}</a>
              <a href="#" className="hover:text-[#6366f1] transition-colors">{t('footer.privacy')}</a>
              <a href="#" className="hover:text-[#6366f1] transition-colors">{t('footer.help')}</a>
            </div>
            <p className="text-xs text-gray-400">
              &copy; {new Date().getFullYear()} NovelAI v3. All rights reserved.
            </p>
            <p className="text-xs text-gray-400 mt-1">
              <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" className="hover:text-[#6366f1] transition-colors">
                浙ICP备17032747号-7
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* ===== Mobile Bottom Tab Bar ===== */}
      <nav className="mobile-bottom-nav md:hidden">
        <div className="flex items-center justify-around h-16 px-1 max-w-lg mx-auto">
          {mobilePrimaryLinks.map((link) => {
            const Icon = link.icon
            const active = isActive(link.path)
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`mobile-nav-item ${active ? 'active' : ''}`}
              >
                <Icon />
                <span>{link.label}</span>
              </Link>
            )
          })}
          {/* More button */}
          <button
            onClick={() => setMoreOpen(true)}
            className={`mobile-nav-item ${isMoreActive ? 'active' : ''}`}
          >
            <MoreHorizontal />
            <span>{t('nav.more')}</span>
          </button>
        </div>
      </nav>

      {/* ===== Mobile FAB (Floating Action Button) ===== */}
      <Link
        to="/write"
        className="mobile-fab md:hidden !right-auto !left-1/2 !-translate-x-1/2 bg-gradient-to-br from-[#6366f1] to-[#818cf8] text-white"
      >
        <PenLine className="w-6 h-6" />
      </Link>

      {/* ===== More Bottom Sheet Drawer ===== */}
      {moreOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-50 md:hidden"
            onClick={() => setMoreOpen(false)}
          />
          {/* Bottom sheet */}
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl md:hidden mobile-bottom-safe">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">{t('nav.more')}</h3>
              <button
                onClick={() => setMoreOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Drawer grid */}
            <div className="grid grid-cols-3 gap-3 p-4 pb-8">
              {moreLinks.map((link) => {
                const Icon = link.icon
                const active = isActive(link.path)
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMoreOpen(false)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl text-xs font-medium transition-colors ${
                      active
                        ? 'bg-[#6366f1]/10 text-[#6366f1]'
                        : 'text-gray-600 hover:bg-gray-50 active:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-center leading-tight">{link.label}</span>
                  </Link>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default UserLayout
